import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { History, ArrowRight, User, Calendar, FileText, DollarSign, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const StatusHistoryModal = ({ isOpen, onClose, clientId, clientName }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && clientId) {
      fetchHistory();
    }
  }, [isOpen, clientId]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/status-history/client/${clientId}`, getAuthHeaders());
      setHistory(response.data);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status, type) => {
    if (type === 'payment') {
      if (status === 'Pago') return 'bg-green-100 text-green-700 border-green-200';
      if (status === 'Não Pago') return 'bg-red-100 text-red-700 border-red-200';
      return 'bg-gray-100 text-gray-700 border-gray-200';
    } else {
      if (status === 'Protocolo') return 'bg-amber-100 text-amber-700 border-amber-200';
      if (status === 'Em Processo de Baixa') return 'bg-blue-100 text-blue-700 border-blue-200';
      if (status === 'Concluído') return 'bg-green-100 text-green-700 border-green-200';
      if (status === 'Revogada') return 'bg-red-100 text-red-700 border-red-200';
      return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getTypeIcon = (type) => {
    if (type === 'payment') return <DollarSign className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  const getTypeLabel = (type) => {
    if (type === 'payment') return 'Pagamento';
    return 'Processo';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh]" data-testid="status-history-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="history-modal-title">
            <History className="w-5 h-5 text-primary" />
            Histórico de Alterações
          </DialogTitle>
          <p className="text-sm text-muted-foreground">{clientName}</p>
        </DialogHeader>

        <ScrollArea className="h-[400px] pr-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : history.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <History className="w-12 h-12 mb-4 opacity-50" />
              <p>Nenhuma alteração registrada</p>
              <p className="text-sm">As alterações de status aparecerão aqui</p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((item, index) => (
                <div
                  key={item.id || index}
                  className="border rounded-lg p-4 bg-card hover:bg-accent/5 transition-colors"
                  data-testid={`history-item-${index}`}
                >
                  {/* Header com tipo e data */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {getTypeIcon(item.status_type)}
                      <Badge variant="outline" className="text-xs">
                        {getTypeLabel(item.status_type)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Calendar className="w-3 h-3" />
                      {formatDate(item.created_at)}
                    </div>
                  </div>

                  {/* Mudança de status */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {item.old_status && (
                      <>
                        <Badge className={`${getStatusColor(item.old_status, item.status_type)} border`}>
                          {item.old_status}
                        </Badge>
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                      </>
                    )}
                    <Badge className={`${getStatusColor(item.new_status, item.status_type)} border`}>
                      {item.new_status}
                    </Badge>
                  </div>

                  {/* Quem alterou */}
                  <div className="flex items-center gap-1 mt-3 text-xs text-muted-foreground">
                    <User className="w-3 h-3" />
                    <span>
                      Alterado por <span className="font-medium text-foreground">{item.changed_by_name}</span>
                      {item.changed_by_role && (
                        <span className="ml-1">
                          ({item.changed_by_role === 'admin' ? 'Administrador' : 'Parceiro'})
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default StatusHistoryModal;
